import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import HomeFigmaTopbar from './HomeFigmaTopbar';
import { appendDictation } from './MicDictationButton';
import { showNotification } from './NotificationManager';
import './HomeLandingHeroFigma.css';

const SpeechRecognitionCtor =
  typeof window !== 'undefined'
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null;
const speechSupported = !!SpeechRecognitionCtor;

/**
 * Figma IPM hero (node 2362:2): full-viewport hero with in-bar navigation
 * wired to the same routes as App.js landing nav.
 */
export default function HomeLandingHeroFigma() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [interim, setInterim] = useState('');
  const [recording, setRecording] = useState(false);
  const [topbarDark, setTopbarDark] = useState(false);
  const heroVideoRef = useRef(null);
  const heroSectionRef = useRef(null);
  const recognitionRef = useRef(null);
  const shouldRestartRef = useRef(false);

  useEffect(() => {
    const video = heroVideoRef.current;
    if (!video) return undefined;
    const tryPlay = () => {
      const p = video.play();
      if (p !== undefined && typeof p.catch === 'function') p.catch(() => {});
    };
    tryPlay();
    const onTabVisible = () => {
      if (document.visibilityState === 'visible') tryPlay();
    };
    document.addEventListener('visibilitychange', onTabVisible);
    return () => document.removeEventListener('visibilitychange', onTabVisible);
  }, []);

  // Flip nav to dark-text variant once the hero has scrolled mostly out of view.
  useEffect(() => {
    let rafId = 0;
    const check = () => {
      const hero = heroSectionRef.current;
      if (!hero) return;
      const rect = hero.getBoundingClientRect();
      // Switch when less than ~80px of the hero remains visible above the fold.
      setTopbarDark(rect.bottom < 80);
    };
    const onScroll = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        check();
      });
    };
    check();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, []);

  // Stop any live dictation and tear down the recognition session.
  const stopVoice = useCallback(() => {
    shouldRestartRef.current = false;
    setRecording(false);
    setInterim('');
    const rec = recognitionRef.current;
    if (rec) { try { rec.stop(); } catch (_) { /* noop */ } }
  }, []);

  useEffect(() => () => {
    shouldRestartRef.current = false;
    const rec = recognitionRef.current;
    if (rec) { try { rec.stop(); } catch (_) { /* noop */ } }
  }, []);

  // Web Speech dictation into the freehand search box (same approach as
  // MicDictationButton - capture final chunks, show interim text live).
  const startVoice = useCallback(() => {
    if (!speechSupported) {
      showNotification('Voice search is not supported in this browser. Please type instead.', 'info');
      return;
    }
    const rec = new SpeechRecognitionCtor();
    rec.lang = (typeof navigator !== 'undefined' && navigator.language) || 'en-US';
    rec.continuous = true;
    rec.interimResults = true;

    rec.onresult = (event) => {
      let interimText = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const chunk = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          const clean = chunk.trim();
          if (clean) setSearchQuery((prev) => appendDictation(prev, clean));
        } else {
          interimText += chunk;
        }
      }
      setInterim(interimText.trim());
    };

    rec.onerror = (event) => {
      if (event.error === 'no-speech' || event.error === 'aborted') return;
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        shouldRestartRef.current = false;
        setRecording(false);
        setInterim('');
        showNotification('Microphone access was blocked. Allow mic access or type instead.', 'error');
        return;
      }
      console.warn('[HomeHero] speech error:', event.error);
    };

    // Chrome ends recognition on silence; restart while the user is still going.
    rec.onend = () => {
      if (shouldRestartRef.current) {
        try { rec.start(); } catch (_) { /* noop */ }
      }
    };

    recognitionRef.current = rec;
    shouldRestartRef.current = true;
    try {
      rec.start();
      setRecording(true);
    } catch (_) {
      shouldRestartRef.current = false;
      setRecording(false);
      showNotification('Could not start voice search. Please type instead.', 'error');
    }
  }, []);

  const toggleVoice = useCallback(() => {
    if (recording) stopVoice();
    else startVoice();
  }, [recording, startVoice, stopVoice]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (recording) stopVoice();
    const q = `${searchQuery} ${interim}`.trim();
    if (q) navigate(`/search?q=${encodeURIComponent(q)}`, { state: { searchQuery: q, heroIntent: 'buy' } });
  };

  return (
    <section className="home-figma" aria-label={t('nav.home')} ref={heroSectionRef}>
      <HomeFigmaTopbar darkMode={topbarDark} />

      <div className="home-figma__bg" aria-hidden>
        <video
          ref={heroVideoRef}
          className="home-figma__bg-video"
          src="/landing-hero-video.mp4"
          poster="/landing-hero-main.png?v=2"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          disablePictureInPicture
          aria-hidden
        />
        <div className="home-figma__bg-overlay" />
        <div className="home-figma__bg-vignette" />
      </div>

      <div className="home-figma__main">
        <div className="home-figma__copy">
          {/* One sentence: plain + accent spans keep separate typography */}
          <h1 className="home-figma__headline">
            <span className="home-figma__headline-sentence">
              <span className="home-figma__headline-plain">The Real Estate Market,</span>
              <span className="home-figma__headline-accent"> Reinvented.</span>
            </span>
          </h1>
          <p className="home-figma__tagline">
            <span className="home-figma__tagline-line">
              IPM connects real estate <span className="home-figma__tagline-hit">agents</span>, tenants,{' '}
              <span className="home-figma__tagline-hit">buyers</span>,{' '}
              <span className="home-figma__tagline-hit">investors</span> and{' '}
              <span className="home-figma__tagline-hit">partners</span>,
            </span>
            <br aria-hidden />
            <span className="home-figma__tagline-line home-figma__tagline-line--second">
              in one intelligent ecosystem.
            </span>
          </p>
        </div>

        <form className="home-figma__search" onSubmit={handleSearch}>
          <div className={`home-figma__search-inner${recording ? ' home-figma__search-inner--recording' : ''}`}>
            <button
              type="submit"
              className="home-figma__search-icon-slot home-figma__search-icon-slot--btn"
              aria-label="Search"
            >
              <img
                src="/landing-figma-icons/search.png"
                alt=""
                width={25}
                height={25}
                className="home-figma__search-icon-img"
              />
            </button>
            <input
              type="text"
              className="home-figma__search-input"
              placeholder="Describe the property or opportunity you are looking for"
              value={interim ? `${searchQuery}${searchQuery ? ' ' : ''}${interim}` : searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search properties"
            />
            <div className="home-figma__search-actions">
              <button type="button" className="home-figma__icon-btn" aria-label="Location">
                <img
                  src="/landing-figma-icons/location.png"
                  alt=""
                  width={25}
                  height={25}
                  className="home-figma__icon-btn-img"
                />
              </button>
              {speechSupported && (
                <button
                  type="button"
                  className={`home-figma__icon-btn${recording ? ' home-figma__icon-btn--recording' : ''}`}
                  aria-label={recording ? 'Stop voice search' : 'Voice search'}
                  aria-pressed={recording}
                  onClick={toggleVoice}
                  title={recording ? 'Stop voice search' : 'Search by voice'}
                >
                  <img
                    src="/landing-figma-icons/microphone.png"
                    alt=""
                    width={25}
                    height={25}
                    className="home-figma__icon-btn-img"
                  />
                </button>
              )}
            </div>
          </div>
          {recording && (
            <div className="home-figma__voice-hint" role="status">
              <span className="home-figma__voice-dot" aria-hidden />
              Listening… describe what you’re looking for, then press search.
            </div>
          )}
        </form>
      </div>
    </section>
  );
}
